'use client';
import React, { useState } from 'react';
import { buildWhatsAppUrl, whatsAppMessages } from '@/data/hospitalConfig';
import { trackEvent } from '@/lib/analytics';
import { X } from 'lucide-react';

/** Official WhatsApp SVG brand icon */
function WhatsAppIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function GlobalWhatsAppButton() {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const url = buildWhatsAppUrl(whatsAppMessages.general());

  return (
    <div
      className="fixed bottom-24 left-6 z-40 flex flex-col items-start gap-2"
      role="complementary"
      aria-label="WhatsApp All Is Well"
    >
      {/* Tooltip — desktop only */}
      {tooltipVisible && (
        <div className="hidden md:flex items-center gap-2 bg-[#1a2a1a] border border-green-500/25 rounded-xl px-3 py-2 shadow-xl mb-1 max-w-[200px]">
          <p className="text-xs text-green-300/90 leading-snug font-medium">
            Chat with All Is Well on WhatsApp
          </p>
          <button
            onClick={() => setDismissed(true)}
            className="text-green-500/40 hover:text-green-400/70 transition-colors flex-shrink-0 ml-1"
            aria-label="Dismiss WhatsApp tooltip"
          >
            <X size={11} />
          </button>
        </div>
      )}

      {/* Main floating button */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent({ event: 'whatsapp_clicked', source: 'global-floating' })}
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => setTooltipVisible(false)}
        className="flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-full shadow-lg shadow-green-900/30 transition-all duration-200 hover:scale-105 active:scale-95 group"
        style={{ padding: '10px 14px' }}
        aria-label="Chat with All Is Well on WhatsApp"
      >
        <WhatsAppIcon size={18} />
        {/* Label — visible on desktop, hidden on mobile (compact) */}
        <span className="hidden md:block text-xs font-bold tracking-widest uppercase whitespace-nowrap">
          WHATSAPP
        </span>
      </a>
    </div>
  );
}
